import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import { KeyIcon, UserIcon, PaintBrushIcon, PhotoIcon } from '@heroicons/react/24/outline';

import API_URL from '../config/api';

const Settings = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  // Settings state
  const [settings, setSettings] = useState({});
  const [settingChanges, setSettingChanges] = useState({});
  const [loadingSettings, setLoadingSettings] = useState(false);
  
  // Hero image upload state
  const [uploadingHero, setUploadingHero] = useState(false);
  const [uploadingITHero, setUploadingITHero] = useState(false);
  const heroFileInputRef = useRef(null);
  const itHeroFileInputRef = useRef(null);

  const isAdmin = user?.role === 'admin';

  // Fetch settings when appearance tab is active
  useEffect(() => {
    if (activeTab === 'appearance' && isAdmin) {
      fetchSettings();
    }
  }, [activeTab, isAdmin]);

  const fetchSettings = async () => {
    try {
      setLoadingSettings(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSettings(data.grouped || {});
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
    } finally {
      setLoadingSettings(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New passwords do not match!');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters!');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword
        })
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Password changed successfully!');
        setShowPasswordModal(false);
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      } else {
        toast.error(data.message || 'Failed to change password');
      }
    } catch (error) {
      toast.error('Error changing password');
    }
  };

  // Get setting value with pending changes
  const getSettingValue = (category, key, defaultValue = '') => {
    const changeKey = `${category}.${key}`;
    if (settingChanges[changeKey]) {
      return settingChanges[changeKey].value;
    }
    const categorySettings = settings[category] || [];
    const setting = categorySettings.find(s => s.setting_key === key);
    return setting?.setting_value || defaultValue;
  };

  // Handle setting change
  const handleSettingChange = (category, key, value) => {
    setSettingChanges(prev => ({
      ...prev,
      [`${category}.${key}`]: { category, key, value }
    }));
  };

  // Save settings
  const saveSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const settingsArray = Object.values(settingChanges);
      
      const response = await fetch(`${API_URL}/api/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ settings: settingsArray })
      });

      if (response.ok) {
        toast.success('Settings saved!');
        setSettingChanges({});
        fetchSettings();
      } else {
        toast.error('Failed to save settings');
      }
    } catch (error) {
      toast.error('Error saving settings');
    }
  };

  // Generic hero image upload handler
  const handleImageUpload = async (file, settingKey, setUploading, fileInputRef) => {
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file (JPEG, PNG, etc.)');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be smaller than 10MB');
      return;
    }

    try {
      setUploading(true);
      const token = localStorage.getItem('token');
      
      // Upload to S3 via CMS endpoint
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', 'website/dashboard');
      
      const uploadRes = await fetch(`${API_URL}/api/cms/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (!uploadRes.ok) {
        throw new Error('Upload failed');
      }
      
      const uploadData = await uploadRes.json();
      const imageUrl = uploadData.url;
      
      // Save the URL to settings
      const saveRes = await fetch(`${API_URL}/api/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          settings: [{ category: 'appearance', key: settingKey, value: imageUrl }]
        })
      });
      
      if (saveRes.ok) {
        toast.success('Hero image uploaded successfully!');
        fetchSettings();
      } else {
        toast.error('Failed to save image URL');
      }
      
    } catch (err) {
      console.error('Upload error:', err);
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Handle dashboard hero image upload
  const handleHeroImageUpload = (e) => {
    handleImageUpload(e.target.files[0], 'hero_image_url', setUploadingHero, heroFileInputRef);
  };

  // Handle IT Support hero image upload
  const handleITHeroImageUpload = (e) => {
    handleImageUpload(e.target.files[0], 'it_support_hero_image_url', setUploadingITHero, itHeroFileInputRef);
  };

  return (
    <div>
      <div className="page-header" style={{ marginBottom: 32 }}>
        <h1 className="page-title" style={{ fontSize: 28, fontWeight: 600, marginBottom: 8 }}>Settings</h1>
        <p style={{ color: '#718096', fontSize: 14 }}>Manage your account settings and preferences</p>
      </div>

      {/* Tabs */}
      <div style={{ 
        display: 'flex', 
        gap: 8, 
        marginBottom: 24, 
        borderBottom: '1px solid #e2e8f0',
        paddingBottom: 0
      }}>
        <button
          onClick={() => setActiveTab('profile')}
          style={{
            padding: '12px 20px',
            background: activeTab === 'profile' ? '#2B7FE0' : 'transparent',
            color: activeTab === 'profile' ? 'white' : '#4a5568',
            border: 'none',
            borderRadius: '8px 8px 0 0',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            fontWeight: 500
          }}
        >
          <UserIcon style={{ width: 18, height: 18 }} />
          Profile
        </button>
        {isAdmin && (
          <button
            onClick={() => setActiveTab('appearance')}
            style={{
              padding: '12px 20px',
              background: activeTab === 'appearance' ? '#2B7FE0' : 'transparent',
              color: activeTab === 'appearance' ? 'white' : '#4a5568',
              border: 'none',
              borderRadius: '8px 8px 0 0',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontWeight: 500
            }}
          >
            <PaintBrushIcon style={{ width: 18, height: 18 }} />
            Appearance
          </button>
        )}
      </div>

      {/* Profile Tab */}
      {activeTab === 'profile' && (
        <>
          {/* Profile Information Card */}
          <div className="card" style={{ marginBottom: 24 }}>
            <div className="card-header">
              <h2 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <UserIcon style={{ width: 20, height: 20 }} />
                Profile Information
              </h2>
            </div>
            <div className="card-body">
              <div style={{ display: 'grid', gap: 16 }}>
                <div>
                  <label style={{ fontSize: 12, color: '#718096', textTransform: 'uppercase', letterSpacing: 0.5 }}>Name</label>
                  <p style={{ fontSize: 16, fontWeight: 500, marginTop: 4 }}>{user?.name}</p>
                </div>
                <div>
                  <label style={{ fontSize: 12, color: '#718096', textTransform: 'uppercase', letterSpacing: 0.5 }}>Email</label>
                  <p style={{ fontSize: 16, fontWeight: 500, marginTop: 4 }}>{user?.email}</p>
                </div>
                <div>
                  <label style={{ fontSize: 12, color: '#718096', textTransform: 'uppercase', letterSpacing: 0.5 }}>Role</label>
                  <p style={{ fontSize: 16, fontWeight: 500, marginTop: 4, textTransform: 'capitalize' }}>{user?.role}</p>
                </div>
                <div>
                  <label style={{ fontSize: 12, color: '#718096', textTransform: 'uppercase', letterSpacing: 0.5 }}>Member Since</label>
                  <p style={{ fontSize: 16, fontWeight: 500, marginTop: 4 }}>
                    {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Security Settings Card */}
          <div className="card">
            <div className="card-header">
              <h2 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <KeyIcon style={{ width: 20, height: 20 }} />
                Security Settings
              </h2>
            </div>
            <div className="card-body">
              <p style={{ color: '#718096', marginBottom: 20 }}>
                Keep your account secure by regularly updating your password.
              </p>
              <button 
                onClick={() => setShowPasswordModal(true)}
                className="btn btn-primary"
                style={{ 
                  background: '#2B7FE0', 
                  color: 'white', 
                  padding: '10px 20px', 
                  borderRadius: 6, 
                  border: 'none', 
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8
                }}
              >
                <KeyIcon style={{ width: 18, height: 18 }} />
                Change Password
              </button>
            </div>
          </div>
        </>
      )}

      {/* Appearance Tab */}
      {activeTab === 'appearance' && isAdmin && (
        <div>
          {/* Save Bar */}
          {Object.keys(settingChanges).length > 0 && (
            <div style={{
              background: '#2B7FE0',
              color: 'white',
              padding: '12px 20px',
              borderRadius: 8,
              marginBottom: 20,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span>You have {Object.keys(settingChanges).length} unsaved change(s)</span>
              <button
                onClick={saveSettings}
                style={{
                  background: 'white',
                  color: '#2B7FE0',
                  padding: '8px 16px',
                  borderRadius: 6,
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: 600
                }}
              >
                💾 Save Changes
              </button>
            </div>
          )}

          {/* Dashboard Hero Image Card */}
          <div className="card" style={{ marginBottom: 24 }}>
            <div className="card-header">
              <h2 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <PhotoIcon style={{ width: 20, height: 20 }} />
                Dashboard Hero Image
              </h2>
            </div>
            <div className="card-body">
              {loadingSettings ? (
                <div style={{ padding: 20, textAlign: 'center', color: '#718096' }}>Loading...</div>
              ) : (
                <>
                  {/* Upload Button */}
                  <div style={{ marginBottom: 20 }}>
                    <label style={{ display: 'block', marginBottom: 8, fontWeight: 500, fontSize: 14 }}>
                      Upload Image
                    </label>
                    <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                      <input 
                        type="file"
                        ref={heroFileInputRef}
                        accept="image/jpeg,image/png,image/gif,image/webp"
                        onChange={handleHeroImageUpload}
                        style={{ display: 'none' }}
                        id="hero-upload"
                      />
                      <button 
                        type="button"
                        onClick={() => heroFileInputRef.current?.click()}
                        disabled={uploadingHero}
                        style={{
                          background: '#2B7FE0',
                          color: 'white',
                          padding: '10px 20px',
                          borderRadius: 6,
                          border: 'none',
                          cursor: uploadingHero ? 'not-allowed' : 'pointer',
                          opacity: uploadingHero ? 0.7 : 1,
                          minWidth: 160
                        }}
                      >
                        {uploadingHero ? '⏳ Uploading...' : '📤 Upload Image'}
                      </button>
                      <span style={{ color: '#718096', fontSize: 14 }}>JPEG, PNG, GIF, WebP (max 10MB)</span>
                    </div>
                  </div>

                  <div style={{ 
                    margin: '20px 0', 
                    borderTop: '1px solid #e2e8f0', 
                    paddingTop: 20,
                    textAlign: 'center',
                    color: '#718096',
                    fontSize: 14
                  }}>
                    — OR enter a URL directly —
                  </div>

                  {/* URL Input */}
                  <div style={{ marginBottom: 20 }}>
                    <label style={{ display: 'block', marginBottom: 8, fontWeight: 500, fontSize: 14 }}>
                      Image URL
                    </label>
                    <input
                      type="text"
                      value={getSettingValue('appearance', 'hero_image_url', 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1600&h=400&fit=crop&crop=center&q=80')}
                      onChange={(e) => handleSettingChange('appearance', 'hero_image_url', e.target.value)}
                      placeholder="https://example.com/image.jpg"
                      style={{
                        width: '100%',
                        padding: 10,
                        borderRadius: 6,
                        border: '1px solid #e2e8f0',
                        fontSize: 14
                      }}
                    />
                  </div>

                  {/* Preview */}
                  <div style={{ marginTop: 24 }}>
                    <label style={{ display: 'block', marginBottom: 8, fontWeight: 500, fontSize: 14 }}>
                      Preview
                    </label>
                    <div style={{
                      height: 140,
                      borderRadius: 12,
                      backgroundImage: `linear-gradient(135deg, rgba(43, 127, 224, 0.45) 0%, rgba(30, 90, 160, 0.4) 50%, rgba(20, 60, 120, 0.45) 100%), url('${getSettingValue('appearance', 'hero_image_url', 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1600&h=400&fit=crop&crop=center&q=80')}')`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center center',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: 24,
                      fontWeight: 'bold',
                      textShadow: '0 2px 4px rgba(0,0,0,0.4)',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                    }}>
                      Welcome back!
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* IT Support Hero Image Card */}
          <div className="card" style={{ marginBottom: 24 }}>
            <div className="card-header">
              <h2 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <PhotoIcon style={{ width: 20, height: 20 }} />
                IT Support Hero Image
              </h2>
            </div>
            <div className="card-body">
              {loadingSettings ? (
                <div style={{ padding: 20, textAlign: 'center', color: '#718096' }}>Loading...</div>
              ) : (
                <>
                  {/* Upload Button */}
                  <div style={{ marginBottom: 20 }}>
                    <label style={{ display: 'block', marginBottom: 8, fontWeight: 500, fontSize: 14 }}>
                      Upload Image
                    </label>
                    <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                      <input 
                        type="file"
                        ref={itHeroFileInputRef}
                        accept="image/jpeg,image/png,image/gif,image/webp"
                        onChange={handleITHeroImageUpload}
                        style={{ display: 'none' }}
                        id="it-hero-upload"
                      />
                      <button 
                        type="button"
                        onClick={() => itHeroFileInputRef.current?.click()}
                        disabled={uploadingITHero}
                        style={{
                          background: '#2B7FE0',
                          color: 'white',
                          padding: '10px 20px',
                          borderRadius: 6,
                          border: 'none',
                          cursor: uploadingITHero ? 'not-allowed' : 'pointer',
                          opacity: uploadingITHero ? 0.7 : 1,
                          minWidth: 160
                        }}
                      >
                        {uploadingITHero ? '⏳ Uploading...' : '📤 Upload Image'}
                      </button>
                      <span style={{ color: '#718096', fontSize: 14 }}>JPEG, PNG, GIF, WebP (max 10MB)</span>
                    </div>
                  </div>

                  <div style={{ 
                    margin: '20px 0', 
                    borderTop: '1px solid #e2e8f0', 
                    paddingTop: 20,
                    textAlign: 'center',
                    color: '#718096',
                    fontSize: 14
                  }}>
                    — OR enter a URL directly —
                  </div>

                  {/* URL Input */}
                  <div style={{ marginBottom: 20 }}>
                    <label style={{ display: 'block', marginBottom: 8, fontWeight: 500, fontSize: 14 }}>
                      Image URL
                    </label>
                    <input
                      type="text"
                      value={getSettingValue('appearance', 'it_support_hero_image_url', 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=1600&h=400&fit=crop&crop=center&q=80')}
                      onChange={(e) => handleSettingChange('appearance', 'it_support_hero_image_url', e.target.value)}
                      placeholder="https://example.com/image.jpg"
                      style={{
                        width: '100%',
                        padding: 10,
                        borderRadius: 6,
                        border: '1px solid #e2e8f0',
                        fontSize: 14
                      }}
                    />
                  </div>

                  {/* Preview */}
                  <div style={{ marginTop: 24 }}>
                    <label style={{ display: 'block', marginBottom: 8, fontWeight: 500, fontSize: 14 }}>
                      Preview
                    </label>
                    <div style={{
                      height: 120,
                      borderRadius: 12,
                      backgroundImage: `linear-gradient(135deg, rgba(43, 127, 224, 0.45) 0%, rgba(30, 90, 160, 0.4) 50%, rgba(20, 60, 120, 0.45) 100%), url('${getSettingValue('appearance', 'it_support_hero_image_url', 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=1600&h=400&fit=crop&crop=center&q=80')}')`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center center',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: 24,
                      fontWeight: 'bold',
                      textShadow: '0 2px 4px rgba(0,0,0,0.4)',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                    }}>
                      IT Support
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Tips */}
          <div style={{
            padding: 16,
            background: '#f7fafc',
            borderRadius: 8,
            border: '1px solid #e2e8f0'
          }}>
            <h4 style={{ fontWeight: 600, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
              💡 Image Tips
            </h4>
            <ul style={{ margin: 0, paddingLeft: 20, color: '#4a5568', fontSize: 14, lineHeight: 1.8 }}>
              <li><strong>Upload:</strong> Click "Upload Image" to upload directly from your computer</li>
              <li><strong>Unsplash:</strong> Free photos at unsplash.com/s/photos/technology</li>
              <li><strong>Size:</strong> 1600x400 pixels or larger recommended</li>
              <li><strong>Format:</strong> JPG or PNG work best</li>
            </ul>
          </div>
        </div>
      )}

      {/* Password Change Modal */}
      {showPasswordModal && (
        <div 
          className="modal-overlay" 
          onClick={() => setShowPasswordModal(false)}
          style={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            background: 'rgba(0,0,0,0.5)', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            zIndex: 1000 
          }}
        >
          <div 
            className="modal-content" 
            onClick={e => e.stopPropagation()}
            style={{ 
              background: 'white', 
              borderRadius: 8, 
              padding: 24, 
              width: '90%', 
              maxWidth: 400 
            }}
          >
            <h2 style={{ marginBottom: 20, fontSize: 20, fontWeight: 600 }}>Change Password</h2>
            <form onSubmit={handlePasswordChange}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 500, fontSize: 14 }}>
                  Current Password *
                </label>
                <input
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                  required
                  style={{ 
                    width: '100%', 
                    padding: 10, 
                    borderRadius: 6, 
                    border: '1px solid #e2e8f0',
                    fontSize: 14
                  }}
                />
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 500, fontSize: 14 }}>
                  New Password * (min 6 characters)
                </label>
                <input
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                  required
                  minLength="6"
                  style={{ 
                    width: '100%', 
                    padding: 10, 
                    borderRadius: 6, 
                    border: '1px solid #e2e8f0',
                    fontSize: 14
                  }}
                />
              </div>

              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 500, fontSize: 14 }}>
                  Confirm New Password *
                </label>
                <input
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                  required
                  minLength="6"
                  style={{ 
                    width: '100%', 
                    padding: 10, 
                    borderRadius: 6, 
                    border: '1px solid #e2e8f0',
                    fontSize: 14
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button 
                  type="button" 
                  onClick={() => setShowPasswordModal(false)}
                  style={{ 
                    padding: '10px 20px', 
                    borderRadius: 6, 
                    border: '1px solid #e2e8f0', 
                    background: 'white', 
                    cursor: 'pointer',
                    fontSize: 14
                  }}
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  style={{ 
                    background: '#2B7FE0', 
                    color: 'white', 
                    padding: '10px 20px', 
                    borderRadius: 6, 
                    border: 'none', 
                    cursor: 'pointer',
                    fontSize: 14,
                    fontWeight: 500
                  }}
                >
                  Update Password
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
